import React from "react";
import { styled } from "styled-components";
import { useNavigate } from "react-router-dom";
import bgHero from "../assets/bg-hero.webp";
import bgHeroMobile from "../assets/bg-hero-mobile.webp";
import strandritBg from "../assets/strandrit-card.webp";
import airbnbBg from "../assets/airbnb-card.webp";
import fotoBg from "../assets/foto-card.webp";

const HeaderWrapper = styled.header`
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
  margin: 0;
  padding: 32px 16px;

  @media (max-width: 768px) {
    min-height: auto;
    padding: 72px 16px 32px;
  }
`;

const HeroImage = styled.picture`
  position: absolute;
  inset: 0;
  z-index: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
  }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.38);
  z-index: 1;
`;

const Content = styled.div`
  width: min(1120px, 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  position: relative;
  z-index: 2;
`;

const TextContainer = styled.div`
  background: rgba(255, 255, 255, 0.18);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  padding: 28px;
  border-radius: 18px;
  box-shadow: 0 18px 40px rgba(0, 0, 0, 0.18);
  max-width: 820px;
  text-align: center;

  h1 {
    margin: 0 0 12px;
    font-family: Georgia, "Times New Roman", serif;
    font-size: clamp(2.2rem, 5vw, 3.6rem);
    line-height: 1.05;
    color: #f6d15c;
  }

  p {
    margin: 0;
    font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
    font-size: clamp(1rem, 2vw, 1.22rem);
    line-height: 1.6;
    color: #fff;
  }

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const QuickLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-top: 18px;
`;

const QuickLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  padding: 0 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.28);
  color: #fff;
  text-decoration: none;
  font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  font-size: 0.95rem;
  font-weight: 700;

  &:hover {
    background: rgba(255, 255, 255, 0.24);
  }
`;

const ButtonContainer = styled.div`
  width: min(1040px, 100%);
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    max-width: 340px;
  }
`;

const ButtonCard = styled.button`
  position: relative;
  height: 170px;
  width: 100%;
  border-radius: 18px;
  box-shadow: 0 14px 28px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border: 0;
  padding: 0;
  background: #1b1b1b;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 34px rgba(0, 0, 0, 0.24);
  }

  @media (max-width: 768px) {
    height: 136px;
  }
`;

const CardImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const CardShade = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.08) 28%, rgba(0, 0, 0, 0.64) 100%);
`;

const CardLabel = styled.span`
  position: absolute;
  left: 18px;
  right: 18px;
  bottom: 16px;
  z-index: 2;
  color: #fff;
  font-size: 1.25rem;
  font-weight: 700;
  font-family: Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  text-align: left;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.35);

  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const Header = () => {
  const navigate = useNavigate();

  const handleExternalClick = (url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <HeaderWrapper>
      <HeroImage aria-hidden="true">
        <source media="(max-width: 768px)" srcSet={bgHeroMobile} />
        <img src={bgHero} alt="" fetchPriority="high" decoding="async" />
      </HeroImage>
      <Overlay />
      <Content>
        <TextContainer>
          <h1>Welkom bij Manege De IJslander</h1>
          <p>
            Met liefde hebben wij deze pagina voor u gemaakt om u een kleine indruk te geven van wat ik
            (Rozemarijn Ideler, eigenaresse) en mijn team voor u kunnen verzorgen. Geniet van een fantastische
            rit door de Zeeuwse akkers, bos, duinen en strand.
          </p>
          <QuickLinks>
            <QuickLink href="/veelgestelde-vragen.html">Veelgestelde vragen</QuickLink>
            <QuickLink href="/locatie.html">Locatie en aankomst</QuickLink>
          </QuickLinks>
        </TextContainer>
        <ButtonContainer>
          <ButtonCard onClick={() => handleExternalClick("https://boeken.deijslander.nl/")}>
            <CardImage src={strandritBg} alt="" loading="eager" decoding="async" />
            <CardShade />
            <CardLabel>Strandrit Boeken</CardLabel>
          </ButtonCard>
          <ButtonCard onClick={() => handleExternalClick("https://www.airbnb.nl/rooms/18662460")}>
            <CardImage src={airbnbBg} alt="" loading="lazy" decoding="async" />
            <CardShade />
            <CardLabel>Airbnb</CardLabel>
          </ButtonCard>
          <ButtonCard onClick={() => navigate("/fotos")}>
            <CardImage src={fotoBg} alt="" loading="lazy" decoding="async" />
            <CardShade />
            <CardLabel>Foto&apos;s</CardLabel>
          </ButtonCard>
        </ButtonContainer>
      </Content>
    </HeaderWrapper>
  );
};

export default Header;
